import { Input, Radio, Space } from "antd"
import { ColumnType } from "antd/lib/table"
import { useContext, useState } from "react"
import AddRemove from "./AddRemove"
import BudgetCalculatorContext from "./BudgetCalculatorContext"
import { Dispatch, formatDollarsAndCents } from "./budgetCalculatorModel"
import BudgetInputNumber from "./BudgetInputNumber"
import BudgetStatistic from "./BudgetStatistic"
import BudgetTable from "./BudgetTable"
import { LeasedEquipmentWithStats, MiscEquipment, OwnedEquipment, OwnedEquipmentWithStats, RecoveryStrategy } from "./equipmentModel"

const ownedTable = (items: OwnedEquipmentWithStats[], dispatch: Dispatch) => {
  const columns: ColumnType<OwnedEquipmentWithStats>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (x, record) => <Input value={x} onChange={e => dispatch({ type: 'EQUIPMENT_NAME_CHANGED', id: record.id, payload: e.target.value })} />
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (x, record) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EQUIPMENT_QUANTITY_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Replacement Cost',
      dataIndex: 'purchasePrice',
      key: 'purchasePrice',
      render: (x, record) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EQUIPMENT_PURCHASE_PRICE_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Average Useful Life (Years)',
      dataIndex: 'yearsUsed',
      key: 'yearsUsed',
      render: (x, record) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EQUIPMENT_YEARS_USED_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Sell Price',
      dataIndex: 'sellPrice',
      key: 'sellPrice',
      render: (x, record) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EQUIPMENT_SELL_PRICE_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Overhead / Billable',
      dataIndex: 'recoveryStrategy',
      key: 'recoveryStrategy',
      render: (value: RecoveryStrategy, record: OwnedEquipment) => {
        return <Radio.Group
          options={[{ label: 'Overhead', value: 'OVERHEAD' }, { label: 'Billable', value: 'BILLABLE' }]}
          value={value}
          onChange={e => dispatch({ type: 'EQUIPMENT_RECOVERY_STRATEGY_CHANGED', id: record.id, payload: e.target.value })}
          size="small"
        />
      }
    },
    {
      title: 'Total Billable Hours Per Year',
      dataIndex: 'billableHoursPerYear',
      key: 'billableHoursPerYear',
      render: (x, record) => <BudgetInputNumber value={x} disabled={record.recoveryStrategy === 'OVERHEAD'} onChange={value => dispatch({ type: 'EQUIPMENT_BILLABLE_HOURS_PER_YEAR_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Annual Cost to Own',
      key: 'annualCostToOwn',
      render: (_, record) => formatDollarsAndCents(record.annualCostToOwn)
    },
    {
      title: 'Total Annual Cost',
      key: 'totalAnnualCost',
      render: (_, record) => formatDollarsAndCents(record.totalAnnualCost)
    },
    {
      title: 'Hourly Cost to Run',
      key: 'hourlyCostToRun',
      render: (_, record) => record.recoveryStrategy === 'BILLABLE' ? formatDollarsAndCents(record.hourlyCostToRun) : 'N/A'
    },
    {
      key: 'operations',
      render: (_, record, index) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_EQUIPMENT', category: 'OWNED', index })}
          onRemove={() => dispatch({ type: 'REMOVE_EQUIPMENT', category: 'OWNED', index })}
        />
      }
    }
  ]
  return <BudgetTable dataSource={items} columns={columns} />
}

const leasedTable = (items: LeasedEquipmentWithStats[], dispatch: Dispatch) => {
  const columns: ColumnType<LeasedEquipmentWithStats>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (x, record) => <Input value={x} onChange={e => dispatch({ type: 'EQUIPMENT_NAME_CHANGED', id: record.id, payload: e.target.value })} />
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      render: (x, record) => <BudgetInputNumber value={x} onChange={value => dispatch({ type: 'EQUIPMENT_QUANTITY_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Monthly Payment',
      dataIndex: 'monthlyPayment',
      key: 'monthlyPayment',
      render: (x, record) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EQUIPMENT_MONTHLY_PAYMENT_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Overhead / Billable',
      dataIndex: 'recoveryStrategy',
      key: 'recoveryStrategy',
      render: (value: RecoveryStrategy, record: LeasedEquipmentWithStats) => {
        return <Radio.Group
          options={[{ label: 'Overhead', value: 'OVERHEAD' }, { label: 'Billable', value: 'BILLABLE' }]}
          value={value}
          onChange={e => dispatch({ type: 'EQUIPMENT_RECOVERY_STRATEGY_CHANGED', id: record.id, payload: e.target.value })}
          size="small"
        />
      }
    },
    {
      title: 'Total Billable Hours Per Year',
      dataIndex: 'billableHoursPerYear',
      key: 'billableHoursPerYear',
      render: (x, record) => <BudgetInputNumber value={x} disabled={record.recoveryStrategy === 'OVERHEAD'} onChange={value => dispatch({ type: 'EQUIPMENT_BILLABLE_HOURS_PER_YEAR_CHANGED', id: record.id, payload: value })} />
    },
    {
      title: 'Annual Cost to Own',
      key: 'annualCostToOwn',
      render: (_, record) => formatDollarsAndCents(record.annualCostToOwn)
    },
    {
      title: 'Total Annual Cost',
      key: 'totalAnnualCost',
      render: (_, record) => formatDollarsAndCents(record.totalAnnualCost)
    },
    {
      title: 'Hourly Cost to Run',
      key: 'hourlyCostToRun',
      render: (_, record) => record.recoveryStrategy === 'BILLABLE' ? formatDollarsAndCents(record.hourlyCostToRun) : 'N/A'
    },
    {
      key: 'operations',
      render: (_, record, index) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_EQUIPMENT', category: 'LEASED', index })}
          onRemove={() => dispatch({ type: 'REMOVE_EQUIPMENT', category: 'LEASED', index })}
        />
      }
    }
  ]
  return <BudgetTable dataSource={items} columns={columns} />
}


const miscTable = (items: MiscEquipment[], dispatch: Dispatch) => {
  const columns: ColumnType<MiscEquipment>[] = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (x, record) => <Input value={x} onChange={e => dispatch({ type: 'EQUIPMENT_NAME_CHANGED', id: record.id, payload: e.target.value })} />
    },
    {
      title: 'Annual Cost',
      dataIndex: 'totalAnnualCost',
      key: 'totalAnnualCost',
      render: (x, record) => <BudgetInputNumber value={x} money onChange={value => dispatch({ type: 'EQUIPMENT_ANNUAL_COST_CHANGED', id: record.id, payload: value })} />
    },
    {
      key: 'operations',
      render: (_, record, index) => {
        return <AddRemove
          onAdd={() => dispatch({ type: 'ADD_EQUIPMENT', category: 'MISC', index })}
          onRemove={() => dispatch({ type: 'REMOVE_EQUIPMENT', category: 'MISC', index })}
        />
      }
    }
  ]
  return <BudgetTable dataSource={items} columns={columns} />
}

const Equipment = () => {
  const { summary: { equipmentStatistics, equipmentListWithStats }, dispatch } = useContext(BudgetCalculatorContext)
  const [selectedKey, setSelectedKey] = useState('owned')

  return <Space direction="vertical" align="start" size="large">
    <Space direction="horizontal" size="large">
      <BudgetStatistic title="Total Annual Cost of Overhead Equipment" value={equipmentStatistics.overheadAnnualCost} statisticStyle="dollarsAndCents" />
      <BudgetStatistic title="Total Annual Cost of Billable-by-the-unit Equipment" value={equipmentStatistics.billableAnnualCost} statisticStyle="dollarsAndCents" />
    </Space>

    <Radio.Group
      options={[{ label: 'Owned', value: 'owned' }, { label: 'Leased / Financed', value: 'leased' }, { label: 'Miscellaneous', value: 'misc' }]}
      value={selectedKey}
      onChange={e => setSelectedKey(e.target.value)}
      optionType="button"
      style={{ marginRight: '50px' }}
    />
    {selectedKey === 'owned' && ownedTable(equipmentListWithStats.owned, dispatch)}
    {selectedKey === 'leased' && leasedTable(equipmentListWithStats.leased, dispatch)}
    {selectedKey === 'misc' && miscTable(equipmentListWithStats.misc, dispatch)}
  </Space>
}
export default Equipment
